import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    Typography, 
    Button, 
    Spin,
    message,
    Tooltip,
    Modal,
    Input
} from 'antd';
import { 
    CommentOutlined,
    DownloadOutlined,
    EyeOutlined,
    FolderOpenOutlined,
    MailOutlined,
    LinkOutlined,
    RollbackOutlined,
    EditOutlined,
    HeartFilled,
    HeartOutlined,
    DeleteOutlined,
    SendOutlined
} from '@ant-design/icons';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks/useAuth';
import { isConvertible } from '../../utility/fileTypes';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import { ContactGroupModal } from '../settings';
import '../../styles/messages/MessagePreview.css';

const { Title, Text } = Typography;

const MessagePreview = () => {
    const { t } = useTranslation();
    const { unique_identifier } = useParams();
    const [messageData, setMessageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const [messageReceivers, setMessageReceivers] = useState([]);
    const [systemUrl, setSystemUrl] = useState('');
    const navigate = useNavigate();
    useLocation();
    const { user } = useAuth();
    const messageLinkPath = messageData?.access_identifier || `/link/${unique_identifier}/`;
    const messageLink = `${(systemUrl || window.location.origin).replace(/\/$/, '')}${messageLinkPath.startsWith('/') ? messageLinkPath : '/' + messageLinkPath}`;

    // Resizable columns state (body + attachments)
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('messagepreview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, attachments: 40 };
    });
    const [isResizing, setIsResizing] = useState(false);
    const contentRowRef = useRef(null);

    // Send Link modal state
    const [sendLinkModalVisible, setSendLinkModalVisible] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [sendLinkContactSearch, setSendLinkContactSearch] = useState('');
    const [selectedSendEmails, setSelectedSendEmails] = useState([]);
    const [contactEditModalVisible, setContactEditModalVisible] = useState(false);
    const [contactEditingContact, setContactEditingContact] = useState(null);

    const fetchContacts = useCallback(async () => {
        try {
            const response = await axios.get('/api/compose/contacts/');
            const contactsList = (response.data.contacts || []).map(c => ({ ...c, _type: 'contact' }));
            const groupsList = (response.data.groups || []).map(g => ({ ...g, _type: 'group', firstname: g.group_name || '' }));
            setContacts([...contactsList, ...groupsList]);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchContacts();
        }
    }, [user, fetchContacts]);

    const updateContactsApi = (payload) => axios.post('/api/profile/contacts/update/', payload);

    const formatContactDisplay = (contact) => {
        if (!contact) return '';
        const isGroup = contact._type === 'group' || (contact.email && contact.email.includes(';'));
        if (isGroup) return `${contact.group_name || contact.firstname || ''} : ${contact.email || ''}`.trim();
        if (contact.phone && contact.phone.trim()) return `${contact.email || ''} - ${contact.phone.trim()}`;
        return contact.email || '';
    };

    const filterContacts = (contactList, searchText) => {
        if (!contactList || contactList.length === 0) return [];
        let filtered = contactList;
        if (searchText?.trim()) {
            const lower = searchText.toLowerCase();
            filtered = contactList.filter(c => {
                if (!c) return false;
                const email = (c.email || '').toLowerCase();
                const name = `${c.firstname || ''} ${c.lastname || ''}`.trim().toLowerCase();
                const phone = (c.phone || '').toLowerCase();
                return email.includes(lower) || name.includes(lower) || phone.includes(lower);
            });
        }
        return [...filtered].sort((a, b) => (a.contact_type === 'favorite' ? 0 : 1) - (b.contact_type === 'favorite' ? 0 : 1));
    };

    const filteredSendContacts = filterContacts(contacts, sendLinkContactSearch);

    const handleContactAddFavorite = async (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        try {
            await updateContactsApi({ ids: [contact.id], action: 'add_favorite' });
            setContacts(prev => prev.map(c => (c.id === contact.id && c._type === contact._type) ? { ...c, contact_type: 'favorite' } : c));
            message.success(t('contacts.addedToFavorites'));
        } catch (error) {
            message.error(t('contacts.failedToAddFavorite'));
        }
    };

    const handleContactRemoveFavorite = async (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        try {
            await updateContactsApi({ ids: [contact.id], action: 'remove_favorite' });
            const newType = contact._type === 'group' ? 'groups' : 'recents';
            setContacts(prev => prev.map(c => (c.id === contact.id && c._type === contact._type) ? { ...c, contact_type: newType } : c));
            message.success(t('contacts.removedFromFavorites'));
        } catch (error) {
            message.error(t('contacts.failedToRemoveFavorite'));
        }
    };

    const handleContactEdit = (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        setContactEditingContact(contact);
        setContactEditModalVisible(true);
    };

    const handleContactDelete = (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        Modal.confirm({
            title: t('contacts.deleteContact'),
            content: t('contacts.confirmDelete'),
            okText: t('contacts.delete'),
            okType: 'danger',
            cancelText: t('contacts.cancel'),
            onOk: async () => {
                try {
                    await updateContactsApi({ ids: [contact.id], action: 'delete' });
                    const emailsToRemove = contact.email?.includes(';')
                        ? contact.email.split(';').map(e => e.trim()).filter(e => e)
                        : [contact.email];
                    setContacts(prev => prev.filter(c => !(c.id === contact.id && c._type === contact._type)));
                    setSelectedSendEmails(prev => prev.filter(e => !emailsToRemove.includes(e)));
                    message.success(t('contacts.contactMoved'));
                } catch (error) {
                    message.error(t('contacts.failedToDelete'));
                }
            }
        });
    };

    const handleContactSelectForSend = (contact) => {
        const isGroup = contact.email && contact.email.includes(';');
        const emails = isGroup ? contact.email.split(';').map(e => e.trim()).filter(e => e) : [contact.email];
        const allSelected = emails.every(e => selectedSendEmails.includes(e));
        if (allSelected) {
            setSelectedSendEmails(prev => prev.filter(e => !emails.includes(e)));
        } else {
            setSelectedSendEmails(prev => [...new Set([...prev, ...emails])]);
        }
    };

    const handleSendLinkViaEmail = () => {
        const link = messageLink;
        const subject = encodeURIComponent(messageData?.subject || t('messagePreview.messageLink'));
        const body = encodeURIComponent(`${t('messagePreview.messageLink')}:\n\n${link}`);
        const mailto = `mailto:${selectedSendEmails.join(',')}?subject=${subject}&body=${body}`;
        window.location.href = mailto;
        setSendLinkModalVisible(false);
        setSelectedSendEmails([]);
        setSendLinkContactSearch('');
    };

    const openSendLinkModal = () => {
        setSelectedSendEmails([]);
        setSendLinkContactSearch('');
        setSendLinkModalVisible(true);
    };

    useEffect(() => {
        const fetchMessage = async () => {
            try {
                const response = await axios.get(`/api/link/preview/${unique_identifier}/`);
                setMessageData(response.data.message);
                setAttachments(response.data.message.files || []);
                setMessageReceivers(response.data.message_receivers || []);
                setSystemUrl(response.data.system_url || window.location.origin);
            } catch (error) {
                // Access the error message from the backend response
                const errorMessage = error.response?.data?.error || t('messagePreview.errors.loadFailed');
                message.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchMessage();
    }, [unique_identifier, t]);

    // Handle column resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !contentRowRef.current) return;
            const containerRect = contentRowRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            const attachmentsWidth = 100 - bodyWidth;
            const newWidths = { body: bodyWidth, attachments: attachmentsWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('messagepreview_column_widths', JSON.stringify(newWidths));
        };
        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('message-preview-resizing');
            }
        };
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('message-preview-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('message-preview-resizing');
        };
    }, [isResizing]);

    const handleDownload = async (attachmentId, fileName) => {
        try {
            const response = await axios.get(`/api/messages/download/${attachmentId}/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(t('messagePreview.errors.downloadFailed'));
        }
    };

    const handleDownloadAll = async () => {
        try {
            // Use messageData.unique_identifier to ensure we're using the correct message ID
            const response = await axios.get(`/api/messages/${messageData.unique_identifier}/download-all/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'attachments.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(t('messagePreview.errors.downloadFailed'));
        }
    };

    const handleCopy = (linkToCopy) => {
        try {
            // Check if navigator.clipboard is available
            if (!navigator.clipboard) {
                // Fallback copy method using document.execCommand
                const textArea = document.createElement('textarea');
                textArea.value = linkToCopy;
                document.body.appendChild(textArea);
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                
                if (successful) {
                    message.success(t('messagePreview.linkCopied'));
                } else {
                    message.error(t('messagePreview.errors.copyFailed'));
                }
                return;
            }
            
            // Use the Clipboard API
            navigator.clipboard.writeText(linkToCopy)
                .then(() => message.success(t('messagePreview.linkCopied')))
                .catch((err) => {
                    console.error('Could not copy text: ', err);
                    message.error(t('messagePreview.errors.copyFailed'));
                });
        } catch (err) {
            console.error('Copy error: ', err);
            message.error(t('messagePreview.errors.copyFailed'));
        }
    };

    // Helper function to check if there are multiple recipients
    const hasMultipleRecipients = (recipients) => {
        if (!recipients) return false;
        // Check if there's a comma, semicolon or other separator in the recipients string
        if (typeof recipients === 'string') {
            // Split on common email separators (comma, semicolon, space)
            const emailList = recipients.split(/[,;\s]+/).map(email => email.trim()).filter(email => email !== '');
            return emailList.length > 1;
        }
        // If recipients is an array, check its length
        if (Array.isArray(recipients)) {
            return recipients.length > 1;
        }
        return false;
    };

    const handleReply = () => {
        // Navigate to the reply URL with state to preserve views
        const replyUrl = `/link/reply/${unique_identifier}?action=reply`;
        navigate(replyUrl, { state: { preserveViews: true } });
    };

    const handleReplyAll = () => {
        // Navigate to the reply URL with reply_all flag and state to preserve views
        const replyAllUrl = `/link/reply/${unique_identifier}?reply_all=true&action=reply_all`;
        navigate(replyAllUrl, { state: { preserveViews: true } });
    };

    const handleForward = () => {
        // Navigate to the reply URL with forward flag and state to preserve views
        const forwardUrl = `/link/reply/${unique_identifier}?forward=true&action=forward`;
        navigate(forwardUrl, { state: { preserveViews: true } });
    };

    const handleEdit = async () => {
        try {
            // Call the API to check if the message can be fully edited or limited edited
            const response = await axios.post(`/api/message/edit/${unique_identifier}/`);
            
            if (response.data.can_edit_all) {
                // Show success message for conversion to draft
                message.success(t('messagePreview.convertedToDraft'));
                // For unread messages, navigate to the draft edit form
                navigate(`/message-compose?draft_id=${response.data.draft_id}`);
            } else {
                // For read messages, navigate with limited edit parameters
                const currentValues = response.data.current_values;
                const messageData = response.data.message_data || {};
                
                // Store the limited edit data in localStorage with all message data
                localStorage.setItem('limitedEditMessage', JSON.stringify({
                    messageId: response.data.message_id,
                    self_destruct: currentValues.self_destruct,
                    ttlive: currentValues.ttlive,
                    limitedEdit: true,
                    messageData: messageData
                }));
                
                // Navigate to the message-compose page with a special parameter
                navigate(`/message-compose?limited_edit=${response.data.message_id}`);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error ||
                                t('messagePreview.errors.editFailed');
            message.error(errorMessage);
        }
    };

    const getLinkForEmail = (email) => {
        if (!email || !messageReceivers?.length) return messageLink;
        const receiver = messageReceivers.find(
            r => r.email?.trim().toLowerCase() === email.trim().toLowerCase()
        );
        return receiver?.access_link ?? messageLink;
    };

    const parseEmails = (str) => {
        if (!str || typeof str !== 'string') return [];
        return str.split(/[,;\s]+/).map(e => e.trim()).filter(e => e);
    };

    const renderRecipientWithLink = (label, emailStr) => {
        const emails = parseEmails(emailStr);
        if (emails.length === 0) return null;
        return (
            <div className="message-preview-email-row">
                <Text strong>{label} </Text>
                <div className="message-preview-email-recipients-list">
                    {emails.map((email) => {
                        const recipientLink = getLinkForEmail(email);
                        return (
                            <span key={email} className="message-preview-email-recipient-with-link">
                                <span>{email}</span>
                                {recipientLink && (
                                    <Tooltip
                                        title={recipientLink}
                                        placement="top"
                                        mouseEnterDelay={0.3}
                                        overlayStyle={{ maxWidth: '500px' }}
                                    >
                                        <LinkOutlined
                                            className="message-preview-recipient-link-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopy(recipientLink);
                                            }}
                                            aria-label={t('messagePreview.copyLink')}
                                        />
                                    </Tooltip>
                                )}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="message-preview-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="message-preview-container">
            {/* Back to Mailbox Header */}
            <div className="message-preview-top-header">
                <Button
                    type="text"
                    icon={<RollbackOutlined />}
                    onClick={() => navigate('/profile/mailbox')}
                    className="message-preview-back-to-mailbox-btn"
                >
                    {t('messagePreview.backToMailbox')}
                </Button>
                <div className="message-preview-action-buttons">
                    {messageData && (
                        <>
                            {parseEmails(messageData.recipient).length === 0 &&
                             parseEmails(messageData.cc).length === 0 &&
                             parseEmails(messageData.bcc).length === 0 && (
                                <>
                                    <Button
                                        icon={<LinkOutlined />}
                                        onClick={() => handleCopy(messageLink)}
                                        className="message-preview-action-btn"
                                    >
                                        {t('messagePreview.copyLink')}
                                    </Button>
                                    <Button
                                        icon={<SendOutlined />}
                                        onClick={openSendLinkModal}
                                        className="message-preview-action-btn"
                                    >
                                        {t('messagePreview.sendLink')}
                                    </Button>
                                </>
                            )}
                            <Button 
                                icon={<RollbackOutlined />}
                                onClick={handleReply}
                                className="message-preview-action-btn"
                            >
                                {t('messagePreview.reply')}
                            </Button>
                            {(messageData?.cc || hasMultipleRecipients(messageData?.recipient)) && (
                                <Button 
                                    icon={<RollbackOutlined />}
                                    onClick={handleReplyAll}
                                    className="message-preview-action-btn"
                                >
                                    {t('messagePreview.replyAll')}
                                </Button>
                            )}
                            <Button 
                                icon={<RollbackOutlined className="message-preview-forward-icon" />}
                                onClick={handleForward}
                                className="message-preview-action-btn"
                            >
                                {t('messagePreview.forward')}
                            </Button>
                            {user?.email === messageData.sender && (
                                <Button 
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    className="message-preview-action-btn"
                                >
                                    {t('messagePreview.edit')}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {messageData && (
                <>
                    {/* Detail + Body section - shared white background */}
                    <div className="message-preview-detail-body-wrapper">
                    {messageData.self_destruct && (
                        <div className="message-preview-self-destruct-warning">
                            {t('messagePreview.selfDestructWarning')}
                        </div>
                    )}
                    {/* Email Info Section */}
                    <div className="message-preview-email-info">
                        <div className="message-preview-email-icon-wrapper">
                            <MailOutlined />
                        </div>
                        <div className="message-preview-email-details">
                            <div className="message-preview-email-sender-receiver">
                                <div className="message-preview-email-row">
                                    <Text strong>{t('messagePreview.from')} </Text>
                                    <Text>{messageData.sender}</Text>
                                </div>
                                {renderRecipientWithLink(t('messagePreview.to'), messageData.recipient)}
                            </div>
                            {(messageData.cc || messageData.bcc) && (
                                <div className="message-preview-email-cc-bcc">
                                    {messageData.cc && renderRecipientWithLink(t('messagePreview.cc'), messageData.cc)}
                                    {messageData.bcc && renderRecipientWithLink(t('messagePreview.bcc'), messageData.bcc)}
                                </div>
                            )}
                        </div>

                        {/* Metadata on the right side */}
                        <div className="message-preview-metadata-side">
                            <div className="message-preview-metadata-item">
                                <div className="message-preview-metadata-label">{t('messagePreview.expires')}</div>
                                <div className="message-preview-metadata-value">
                                    {dayjs(messageData.timestamp).format('MMM D, YYYY, h:mm A')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subject Line */}
                    <div className="message-preview-subject-section">
                        <Title level={3} className="message-preview-subject-title">
                            {messageData.subject}
                        </Title>
                    </div>

                    {/* Two-column layout: Body + Attachments */}
                    <div className="message-preview-content-row" ref={contentRowRef}>
                        {/* Message Body - Left Column */}
                        <div 
                            className="message-preview-body-column"
                            style={{ width: attachments.length > 0 ? `${columnWidths.body}%` : '100%' }}
                        >
                            <div className="message-preview-body-section">
                                <div 
                                    className="message-preview-body-content"
                                    dangerouslySetInnerHTML={{ __html: messageData.body }}
                                />
                            </div>
                        </div>

                        {/* Resize Handle */}
                        {attachments.length > 0 && (
                            <div 
                                className="message-preview-resize-handle"
                                onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
                            />
                        )}

                        {/* Attachments - Right Column */}
                        {attachments.length > 0 && (
                            <div 
                                className="message-preview-attachments-column"
                                style={{ width: `${columnWidths.attachments}%` }}
                            >
                                <div className="message-preview-attachments-section">
                                    <div className="message-preview-attachments-section-header">
                                        <span className="message-preview-attachments-section-label">
                                            {t('messagePreview.attachments', { count: attachments.length })}
                                        </span>
                                        <Tooltip title={t('messages.view.downloadAll')}>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<FolderOpenOutlined />}
                                                onClick={handleDownloadAll}
                                                className="message-preview-attachments-download-all-btn"
                                            />
                                        </Tooltip>
                                    </div>
                                    <div className="message-preview-attachments-list message-preview-attachments-list-rows">
                                        {attachments.map((attachment, index) => {
                                            const fileSize = attachment.file_size;
                                            const formattedSize = typeof fileSize === 'number'
                                                ? fileSize >= 1024 * 1024
                                                    ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
                                                    : `${(fileSize / 1024).toFixed(2)} KB`
                                                : fileSize || '-';
                                            const fileName = attachment.file_name;
                                            const canPreview = isConvertible(fileName);
                                            const commentCount = attachment.comment_count ?? 0;
                                            return (
                                                <div
                                                    key={attachment.unique_identifier || index}
                                                    className={`message-preview-attachment-row ${canPreview ? 'message-preview-attachment-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'message-preview-attachment-row-has-comments' : ''}`}
                                                    onClick={canPreview ? () => setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' }) : undefined}
                                                    role={canPreview ? 'button' : undefined}
                                                    tabIndex={canPreview ? 0 : undefined}
                                                    onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' }); } } : undefined}
                                                >
                                                    {canPreview && commentCount > 0 && (
                                                        <span
                                                            className="message-preview-attachment-comment-badge"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' });
                                                            }}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if ((e.key === 'Enter' || e.key === ' ') && commentCount > 0) {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' });
                                                                }
                                                            }}
                                                            aria-label={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
                                                        >
                                                            <CommentOutlined />
                                                            <span className="message-preview-attachment-comment-badge-count">{commentCount}</span>
                                                        </span>
                                                    )}
                                                    <div className="message-preview-attachment-title-cell">
                                                        <span className="message-preview-attachment-row-title">
                                                            {fileName}
                                                        </span>
                                                        <span className="message-preview-attachment-row-filesize">
                                                            {formattedSize}
                                                        </span>
                                                    </div>
                                                    <div className="message-preview-attachment-actions">
                                                        {canPreview && (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                className="message-preview-attachment-preview-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' });
                                                                }}
                                                            />
                                                        )}
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<DownloadOutlined />}
                                                            className="message-preview-attachment-download-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownload(attachment.unique_identifier, fileName);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    </div>
                </>
            )}

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => setAttachmentPreviewDoc(null)}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                source="messages"
                onDownload={attachmentPreviewDoc ? () => handleDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName) : undefined}
            />

            {/* Send Link modal */}
            <Modal
                title={t('messagePreview.sendLink')}
                open={sendLinkModalVisible}
                onCancel={() => { setSendLinkModalVisible(false); setSelectedSendEmails([]); setSendLinkContactSearch(''); }}
                footer={[
                    <Button key="cancel" onClick={() => { setSendLinkModalVisible(false); setSelectedSendEmails([]); setSendLinkContactSearch(''); }}>
                        {t('contacts.cancel')}
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendLinkViaEmail}
                        disabled={selectedSendEmails.length === 0}
                    >
                        {t('messagePreview.sendLink')}
                    </Button>
                ]}
                width={520}
                className="message-preview-send-link-modal"
            >
                <Input.Search
                    placeholder={t('contacts.searchContacts')}
                    value={sendLinkContactSearch}
                    onChange={(e) => setSendLinkContactSearch(e.target.value)}
                    allowClear
                    style={{ marginBottom: 16 }}
                />
                <div className="message-preview-send-link-contacts-list">
                    {filteredSendContacts.length === 0 ? (
                        <div className="message-preview-send-link-empty">{t('contacts.noContacts')}</div>
                    ) : (
                        filteredSendContacts.map((contact) => {
                            const isGroup = contact.email && contact.email.includes(';');
                            const emails = isGroup ? contact.email.split(';').map(e => e.trim()).filter(e => e) : [contact.email];
                            const isSelected = emails.every(e => selectedSendEmails.includes(e));
                            const isFavorite = contact.contact_type === 'favorite';
                            const displayContent = formatContactDisplay(contact);
                            const tooltipContent = isGroup && contact.email
                                ? contact.email.split(';').map(e => e.trim()).join('\n')
                                : null;
                            return (
                                <Tooltip key={contact.id || contact.email} title={tooltipContent ? <div style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</div> : null} placement="right">
                                    <div
                                        className={`message-preview-send-link-contact-row ${isSelected ? 'message-preview-send-link-contact-row-selected' : ''}`}
                                        onClick={() => handleContactSelectForSend(contact)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleContactSelectForSend(contact); } }}
                                    >
                                        <div className="message-preview-send-link-contact-title-cell">
                                            <span className="message-preview-send-link-contact-title">
                                                {isFavorite && <HeartFilled className="message-preview-send-link-contact-favorite-icon" />}
                                                {displayContent}
                                            </span>
                                        </div>
                                        <div className="message-preview-send-link-contact-actions" onClick={e => e.stopPropagation()}>
                                            <span className="message-preview-send-link-contact-action" onClick={e => isFavorite ? handleContactRemoveFavorite(contact, e) : handleContactAddFavorite(contact, e)}>
                                                {isFavorite ? <HeartFilled /> : <HeartOutlined />}
                                            </span>
                                            <span className="message-preview-send-link-contact-action" onClick={e => handleContactEdit(contact, e)}>
                                                <EditOutlined />
                                            </span>
                                            <span className="message-preview-send-link-contact-action" onClick={e => handleContactDelete(contact, e)}>
                                                <DeleteOutlined />
                                            </span>
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        })
                    )}
                </div>
                {selectedSendEmails.length > 0 && (
                    <div className="message-preview-send-link-selected-count">
                        {selectedSendEmails.length} {selectedSendEmails.length === 1 ? t('messagePreview.recipientSelected') : t('messagePreview.recipientsSelected')}
                    </div>
                )}
            </Modal>

            <ContactGroupModal
                visible={contactEditModalVisible}
                onClose={() => { setContactEditModalVisible(false); setContactEditingContact(null); }}
                onAdded={fetchContacts}
                type="contact"
                availableContacts={contacts}
                editingContact={contactEditingContact}
            />
        </div>
    );
};

export default MessagePreview;